/**
 * useGuidance.ts
 *
 * Evaluates and surfaces one guidance balloon per day.
 * Only fires when no celebration has been shown today.
 *
 * Guards (same pattern as useCelebration):
 *   - Daily gate via localStorage
 *   - Once-per-session module-level set
 *   - In-flight ref to prevent concurrent evaluation
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  evaluateGuidance,
  hasShownGuidanceToday,
  type GuidanceResult,
} from '../services/guidance.service';
import { hasShownCelebrationToday } from '../services/celebration.service';

const _evaluatedThisSession = new Set<string>();

interface UseGuidanceResult {
  guidance: GuidanceResult | null;
  dismiss: () => void;
}

/**
 * @param userId    - Firebase Auth UID, or undefined if not logged in
 * @param dataReady - true once daily summary data has loaded
 * @param celebrationActive - true if a celebration is currently displayed;
 *                            guidance is suppressed while a celebration is visible
 */
export function useGuidance(
  userId: string | undefined,
  dataReady: boolean,
  celebrationActive: boolean,
): UseGuidanceResult {
  const [guidance, setGuidance] = useState<GuidanceResult | null>(null);
  const evaluatingRef = useRef(false);

  useEffect(() => {
    if (!userId || !dataReady) return;
    if (celebrationActive) return;
    if (hasShownCelebrationToday()) return;
    if (hasShownGuidanceToday()) return;
    if (_evaluatedThisSession.has(userId)) return;
    if (evaluatingRef.current) return;

    evaluatingRef.current = true;
    _evaluatedThisSession.add(userId);

    let cancelled = false;

    async function evaluate() {
      try {
        const result = await evaluateGuidance(userId!);
        if (!cancelled) setGuidance(result);
      } catch (err) {
        console.error('[useGuidance] Evaluation failed:', err);
        if (!cancelled) setGuidance(null);
      } finally {
        evaluatingRef.current = false;
      }
    }

    evaluate();
    return () => { cancelled = true; };
  }, [userId, dataReady, celebrationActive]);

  const dismiss = useCallback(() => setGuidance(null), []);

  return { guidance, dismiss };
}
